import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, Optional, UserRef } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';

type Payload = Loadable<UserRef[]>;

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface AssignableUserStore {
  assignableUsersForOrdersByDomainId: Ref<Record<number, Optional<Payload>>>;
  assignableUsersForShipmentsByDomainId: Ref<Record<number, Optional<Payload>>>;
  loadAssignableUsersByDomainId: (
    domainId: number,
    entityType: 'shipment' | 'order',
    opts?: PerformLoadOptions,
  ) => Payload;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useAssignableUserStore = defineStore(
  'assignableUser',
  (): AssignableUserStore => {
    const { selectedDomainId } = useCurrentUserStore();
    const { t } = useI18n();
    const toast = useToast();

    const assignableUsersForOrdersByDomainId: Ref<
      Record<number, Optional<Payload>>
    > = ref({});

    const assignableUsersForShipmentsByDomainId: Ref<
      Record<number, Optional<Payload>>
    > = ref({});

    const loadAssignableUsersByDomainId = (
      domainId: number,
      entityType: 'shipment' | 'order',
      opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
    ): Payload => {
      const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

      const assignableUsersForEntityType =
        entityType === 'order'
          ? assignableUsersForOrdersByDomainId
          : assignableUsersForShipmentsByDomainId;
      if (!assignableUsersForEntityType.value[domainId]) {
        assignableUsersForEntityType.value[domainId] = { loading: false };
      }

      const assignableUsers = assignableUsersForEntityType.value[domainId];

      const numAssignableUsers = assignableUsers.data?.length ?? 0;
      const isLoading = assignableUsers.loading;
      const requiresLoad = numAssignableUsers === 0 && !isLoading;

      return options.bypassCache || requiresLoad
        ? performLoad(domainId, entityType)
        : assignableUsers;
    };

    const performLoad = (
      domainId: number,
      entityType: 'shipment' | 'order',
    ): Payload => {
      const assignableUsersForEntityType =
        entityType === 'order'
          ? assignableUsersForOrdersByDomainId
          : assignableUsersForShipmentsByDomainId;

      if (!assignableUsersForEntityType.value[domainId]) {
        assignableUsersForEntityType.value[domainId] = { loading: false };
      }

      const assignableUsers = assignableUsersForEntityType.value[domainId];
      assignableUsers.loading = true;
      assignableUsers.error = undefined;

      api.user
        .findAllAssignableUsersForDomain(domainId, entityType)
        .then((result) => {
          assignableUsers.data = result;
        })
        .catch((e) => {
          if (axios.isCancel(e)) {
            return;
          }

          assignableUsers.error = e;
          console.error('Error loading assignable users', e);
          toast.addError({
            detail: t('errors.loadingParam', {
              param: t('users.users', 2),
            }),
            error: e,
          });
        })
        .finally(() => {
          assignableUsers.loading = false;
        });

      return assignableUsers;
    };

    watch(selectedDomainId, () => {
      assignableUsersForOrdersByDomainId.value = {};
      assignableUsersForShipmentsByDomainId.value = {};
    });

    return {
      assignableUsersForOrdersByDomainId,
      assignableUsersForShipmentsByDomainId,
      loadAssignableUsersByDomainId,
    };
  },
);

export const useAssignableUserStore = (): AssignableUserStore => {
  const store = _useAssignableUserStore();
  const {
    assignableUsersForOrdersByDomainId,
    assignableUsersForShipmentsByDomainId,
  } = storeToRefs(store);
  const { loadAssignableUsersByDomainId } = store;

  return {
    assignableUsersForOrdersByDomainId,
    assignableUsersForShipmentsByDomainId,
    loadAssignableUsersByDomainId,
  };
};
